// data-current holds the index of the item up front and the css places all
// three from it. Link hover, link focus and the mobile timer all just move
// that index, inside a view transition so the items animate between slots.
// Each item is an image with its video laid over it, and the video is faded up
// and back over that image as the item takes the front and leaves it.
const gallery = document.querySelector('#expertise-gallery')

if (gallery) {
  const MOBILE_WIDTH = 1000
  const AUTOPLAY_DELAY = 4000

  const section = gallery.closest('.expertise-hero')
  const items = [...gallery.querySelectorAll('.media-item')]
  const videos = items.map(item => item.querySelector('video'))
  // the still under each video, and what the css keys the fade off
  const posters = items.map(item => item.querySelector('img'))
  const links = [...section.querySelectorAll('.links-wrapper a')]
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

  let autoplayTimer
  let resizeTimer
  let pendingVideo
  let activeTransition = null
  let pendingIndex = null
  let onScreen = false

  function currentIndex() {
    return Number(gallery.dataset.current) || 0
  }

  function activate(index) {
    if (!items[index]) return

    // a second transition skips the one in flight and drops the items wherever
    // they had got to, and a pointer moving along the links passes the ones in
    // between, so hold the last index asked for until the current move is done
    if (activeTransition) {
      pendingIndex = index
      return
    }

    if (index === currentIndex()) return

    clearAutoplay()

    // The incoming video starts as the move does, alongside the fade that
    // brings it up over its poster. One with a poster was wound back as it
    // faded out and is already on its first frame; one without has nowhere to
    // hide, so it takes that jump here, where the move covers it.
    const incoming = videos[index]

    if (incoming) {
      if (!posters[index]) incoming.currentTime = 0

      incoming.play().catch(() => {})
    }

    const mutate = () => {
      gallery.dataset.current = index
      // both fades are spent under the movement: the item going back drops to
      // its poster while the one coming up rises off its own
      items.forEach((item, i) => item.classList.toggle('playing', i === index))
    }

    if (!document.startViewTransition) {
      mutate()
      return settle()
    }

    activeTransition = document.startViewTransition(mutate)
    activeTransition.finished.then(settle, settle)
  }

  // the tail of a move: the items behind stop, the timer starts over and an
  // index asked for mid transition is taken up
  function settle() {
    activeTransition = null

    syncPlayback()
    scheduleAutoplay()

    if (pendingIndex === null) return

    const next = pendingIndex
    pendingIndex = null
    activate(next)
  }

  function activateNext() {
    activate((currentIndex() + 1) % items.length)
  }

  // only the item up front plays, and the ones behind are stopped here rather
  // than in the move, since they are still fading out while it runs
  function syncPlayback() {
    const current = currentIndex()

    videos.forEach((video, index) => {
      if (!video) return

      index === current ? video.play().catch(() => {}) : video.pause()
    })
  }

  // only runs on mobile, where there is no hover to drive the gallery, and
  // only on screen, so the page is not snapshotting transitions nobody sees
  function scheduleAutoplay() {
    clearAutoplay()

    if (!onScreen || items.length < 2) return
    if (window.innerWidth > MOBILE_WIDTH || reducedMotion.matches) return

    const video = videos[currentIndex()]
    // the lazy loader sets src on its own schedule, so ask the markup whether
    // this is a video rather than whether it has loaded
    const isVideo = video && (video.src || video.dataset.src)

    if (!isVideo) {
      autoplayTimer = setTimeout(activateNext, AUTOPLAY_DELAY)
      return
    }

    // the markup loops every video for the hover case, so the one being waited
    // on has to stop looping or it would never end
    pendingVideo = video
    video.loop = false
    video.addEventListener('ended', activateNext)

    if (!video.paused && !video.ended) return

    // buffering or refused, and a video that never starts never ends either,
    // so the delay stands in until playback takes the job back over
    autoplayTimer = setTimeout(activateNext, AUTOPLAY_DELAY)
    video.addEventListener('playing', cancelDelay)
    video.play().catch(() => {})
  }

  function cancelDelay() {
    clearTimeout(autoplayTimer)
    autoplayTimer = null
  }

  function clearAutoplay() {
    cancelDelay()

    if (pendingVideo) {
      pendingVideo.removeEventListener('ended', activateNext)
      pendingVideo.removeEventListener('playing', cancelDelay)
      // hand the looping back for the hover case
      pendingVideo.loop = true
      pendingVideo = null
    }
  }

  links.forEach((link, index) => {
    link.addEventListener('mouseenter', () => activate(index))
    // focus keeps the gallery operable by keyboard, the images are not
    link.addEventListener('focus', () => activate(index))
  })

  // A video that has left the front is wound back once it has faded out and the
  // poster is all that shows, so the jump lands out of sight. The fade outlasts
  // the move, so settle has already paused it by then.
  videos.forEach((video, index) => {
    if (!video || !posters[index]) return

    video.addEventListener('transitionend', event => {
      if (event.propertyName !== 'opacity') return
      if (items[index].classList.contains('playing')) return

      video.currentTime = 0
    })
  })

  // the first item is up front from the markup and its video autoplays as the
  // lazy loader reaches it, so it never goes through a move and is marked here
  items[currentIndex()].classList.add('playing')

  // a pointer in the block is driving, so nothing should pull an image away
  section.addEventListener('mouseenter', clearAutoplay)
  section.addEventListener('mouseleave', scheduleAutoplay)

  new IntersectionObserver(([entry]) => {
    onScreen = entry.isIntersecting
    scheduleAutoplay()
  }).observe(gallery)

  // catches rotation and resizing across the breakpoint
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer)
    resizeTimer = setTimeout(scheduleAutoplay, 100)
  })

  reducedMotion.addEventListener('change', scheduleAutoplay)
}
